import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { NotificationInterface, NotificationTarget } from "./models/notification";
import { updateNotifications } from "./service/notification";
import { searchNotifications } from "./service/query/QueryService.full";
import { Status } from "./service/Shared";
import { useAuth } from "./useAuth";
import { mapNotificationResponse } from "./utils";
import { isPollingAbortedError, pollAsync } from "./utils/poll";
import { getNotificationRoute } from "./utils/routes";

const POLL_INTERVAL_MS = 30_000;

export interface NotificationContextType {
  notifications: NotificationInterface[];
  unreadCount: number;
  refetchNotifications: () => void;
  markNotificationRead: (uuid: string) => Promise<void>;
  navigateToNotification: (notification: NotificationTarget) => void;
  markAllNotificationsRead: () => Promise<void>;
  dismissNotification: (uuid: string, wasUnread: boolean) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({} as NotificationContextType);

interface NotificationProviderProps {
  children: JSX.Element;
}

export const NotificationProvider = ({ children }: NotificationProviderProps): JSX.Element => {
  const { user, currentUserType } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationInterface[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [, setFetchError] = useState<Error | null>(null);

  // To handle edge cases where a notification is dismissed or marked read while a poll is in flight
  // Bumped by every mutation. A fetch that started before a mutation carries a stale count,
  // so its response is discarded if the sequence changed while it was in flight.
  const mutationSeqRef = useRef(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchNotifications = (): Promise<any> => {
    const seqAtFetchStart = mutationSeqRef.current;
    return (
      searchNotifications({
        paging: { afterCursor: null, beforeCursor: null, limit: 10 },
        filter: {
          results: {
            deliveryType: { operator: "neq" as const, value: "Email" },
            isDismissed: { operator: "eq" as const, value: false },
          },
        },
        sort: [{ key: "results.createdAt" as const, direction: "desc" as const }],
      })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          // Drop responses from polls that began before a mutation; their counts predate it.
          if (response.status === Status.Success && response.data && mutationSeqRef.current === seqAtFetchStart) {
            setNotifications(response.data.results.map(mapNotificationResponse));
            setUnreadCount(response.data.aspects.unreadCount);
          }
          return response;
        })
    );
  };

  const refetchNotifications = (): void => {
    fetchNotifications().catch((error) => {
      setFetchError(error instanceof Error ? error : new Error("Failed to fetch notifications"));
    });
  };

  // Notification writes live here so the bell's unread count is the single source of truth.
  // Each mutation optimistically updates `unreadCount` for an immediate, race-free bell update;
  // the regular poll reconciles, and a failed write triggers a refetch to restore the true count.
  const markNotificationRead = async (uuid: string): Promise<void> => {
    mutationSeqRef.current += 1;
    setUnreadCount((count) => Math.max(0, count - 1));

    const response = await updateNotifications({
      isDismissed: null,
      isRead: true,
      notificationUuids: [uuid],
      markAllAsRead: null,
    });
    if (response.status !== Status.Success) {
      setFetchError(new Error("Failed to mark notification as read"));
      refetchNotifications();
    }
  };

  /*
    Navigate to the notification's target when it carries a relative url. The caller passes the
    notification itself, since it may live in their own paged/sorted list rather than the provider's
    top-10 `notifications` array. Kept separate from marking read so a notification can be opened
    repeatedly, not just the first (unread) time.

    A task notification also passes its objectUuid through as ?taskUuid=, which opens that task's overlay
    on arrival (KAN-9609), and a quote-expiry reminder passes its quote as ?issuanceTransactionUuid=, which
    opens that quote's issuance in the Unit transactions panel. The url can't supply either: a workflow
    task's link names only its board, and a quote reminder's only its project.
  */
  const navigateToNotification = (notification: NotificationTarget): void => {
    if (!notification.url) return;

    navigate(getNotificationRoute(notification, currentUserType));
  };

  const markAllNotificationsRead = async (): Promise<void> => {
    mutationSeqRef.current += 1;
    setUnreadCount(0);
    const response = await updateNotifications({
      isDismissed: null,
      isRead: null,
      notificationUuids: [],
      markAllAsRead: true,
    });
    if (response.status !== Status.Success) {
      setFetchError(new Error("Failed to mark all notifications as read"));
      refetchNotifications();
    }
  };

  const dismissNotification = async (uuid: string, wasUnread: boolean): Promise<void> => {
    // Always bump: dismissing removes the item from the polled result set, not just the count.
    mutationSeqRef.current += 1;
    if (wasUnread) {
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    const response = await updateNotifications({
      isDismissed: true,
      isRead: null,
      notificationUuids: [uuid],
      markAllAsRead: null,
    });
    if (response.status !== Status.Success) {
      setFetchError(new Error("Failed to dismiss notification"));
      refetchNotifications();
    }
  };

  useEffect(() => {
    if (!user) {
      // Logged out: clear the previous user's notifications so the bell shows no unread count.
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    const controller = new AbortController();

    pollAsync(fetchNotifications, {
      intervalMs: POLL_INTERVAL_MS,
      shouldStopPolling: () => false,
      signal: controller.signal,
    }).catch((error) => {
      if (!isPollingAbortedError(error)) {
        setFetchError(error instanceof Error ? error : new Error("Failed to fetch notifications"));
      }
    });

    return () => {
      controller.abort();
    };
  }, [user?.userUuid]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      refetchNotifications,
      markNotificationRead,
      navigateToNotification,
      markAllNotificationsRead,
      dismissNotification,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notifications, unreadCount]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotificationContext = (): NotificationContextType => useContext(NotificationContext);
